import { transformerNotationErrorLevel } from '@shikijs/transformers'
import { memo, useState } from 'react'
import { useAsync } from 'react-use'
import { createHighlighter, ShikiTransformer } from 'shiki'

interface HighlightedCodeProps {
  code: string
  language: string
}

const SUPPORTED_LANGUAGES = [
  'xml',
  'markdown',
  'yaml',
  'toml',
  'json',
  'java',
  'python',
  'javascript',
  'typescript',
  'tsx',
  'jsx',
  'html',
  'css',
  'sql',
  'bash',
  'shell',
  'csv',
  'plaintext'
]

/**
 * Maps file extensions to Shiki language identifiers for syntax highlighting
 */
const EXTENSION_TO_LANGUAGE: Record<string, string> = {
  json: 'json',
  py: 'python',
  xml: 'xml',
  yaml: 'yaml',
  yml: 'yaml',
  md: 'markdown',
  ts: 'typescript',
  tsx: 'tsx',
  js: 'javascript',
  jsx: 'jsx',
  java: 'java',
  sql: 'sql',
  sh: 'bash',
  bash: 'bash',
  css: 'css',
  html: 'html',
  htm: 'html',
  csv: 'csv',
  toml: 'toml',
  txt: 'plaintext'
}

/**
 * Get the syntax highlighting language from a filename.
 * Returns 'plaintext' for unsupported or unknown file types.
 */
export function getLanguageFromFilename(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || ''
  return EXTENSION_TO_LANGUAGE[ext] || 'plaintext'
}

let highlighterPromise: Promise<any> | null = null

function getHighlighter() {
  if (!highlighterPromise) {
    highlighterPromise = createHighlighter({
      langs: SUPPORTED_LANGUAGES,
      themes: ['github-light']
    })
  }
  return highlighterPromise
}

function HighlightedCodeInner({ code, language }: HighlightedCodeProps) {
  const [html, setHtml] = useState('')

  useAsync(async () => {
    let lang = language
    if (!SUPPORTED_LANGUAGES.includes(language)) {
      lang = 'plaintext'
    }

    const highlighter = await getHighlighter()
    const __html = await highlighter.codeToHtml(code, {
      lang,
      theme: 'github-light',
      transformers: [
        transformerNotationErrorLevel() as ShikiTransformer,
        {
          pre(node) {
            node.properties.style = 'background-color: transparent;padding:8px;'
            return node
          }
        }
      ]
    })
    setHtml(__html)
  }, [code, language])

  return <div dangerouslySetInnerHTML={{ __html: html }} />
}

export const HighlightedCode = memo(HighlightedCodeInner, (prev, next) => prev.code === next.code)
